import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, Search, Megaphone, FileText, Bot, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
import logo from "@/assets/tcs-logo.png";
import { geoCluster, aiAdsCluster, aiContentCluster, aiAgentsCluster } from "@/data/contentClusters";

const pillars = [
  {
    cluster: geoCluster,
    icon: Search,
    color: "text-primary",
    bgColor: "bg-primary/10",
  },
  {
    cluster: aiAdsCluster,
    icon: Megaphone,
    color: "text-orange-500",
    bgColor: "bg-orange-500/10",
  },
  {
    cluster: aiContentCluster,
    icon: FileText,
    color: "text-green-500",
    bgColor: "bg-green-500/10",
  },
  {
    cluster: aiAgentsCluster,
    icon: Bot,
    color: "text-purple-500",
    bgColor: "bg-purple-500/10",
  },
];

const navLinks = [
  { label: "Industries", href: "/industries" },
  { label: "Insights", href: "/insights" },
  { label: "Pricing", href: "/pricing" },
  { label: "About", href: "/about" },
];

export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setMobileServicesOpen(false);
  }, [location]);

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? "glass border-b border-border/50" : "bg-transparent"
        }`}
      >
        <div className="container-wide">
          <nav className="flex items-center justify-between h-16 md:h-20 relative">
            {/* Logo */}
            <Link to="/" className="flex items-center z-10">
              <img src={logo} alt="The Cloud SEO" className="h-8 md:h-10 w-auto dark:invert dark:brightness-200" />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-1 absolute left-1/2 -translate-x-1/2">
              <div className="[&_[data-radix-navigation-menu-viewport]]:hidden">
                <NavigationMenu className="relative">
                  <NavigationMenuList>
                    {/* Services Mega Menu */}
                    <NavigationMenuItem>
                      <NavigationMenuTrigger className="bg-transparent hover:bg-transparent data-[state=open]:bg-transparent">
                        Services
                      </NavigationMenuTrigger>
                      <NavigationMenuContent className="fixed left-1/2 -translate-x-1/2 top-16 md:top-20">
                      <div className="w-[800px] max-w-[calc(100vw-2rem)] p-6 bg-popover border border-border rounded-xl shadow-xl">
                        <div className="grid grid-cols-4 gap-6">
                          {pillars.map(({ cluster, icon: Icon, color, bgColor }) => (
                            <div key={cluster.pillar.slug}>
                              <div className="flex items-center gap-2 mb-3">
                                <div className={cn("p-1.5 rounded-md", bgColor)}>
                                  <Icon className={cn("w-4 h-4", color)} />
                                </div>
                                <Link 
                                  to={`/services/${cluster.pillar.slug}`}
                                  className="font-semibold text-sm hover:text-primary transition-colors"
                                >
                                  {cluster.pillar.title}
                                </Link>
                              </div>
                              <p className="text-xs text-muted-foreground mb-3">
                                {cluster.pillar.subtitle}
                              </p>
                              <ul className="space-y-1.5">
                                {cluster.moneyPages.slice(0, 4).map((page) => (
                                  <li key={page.slug}>
                                    <NavigationMenuLink asChild>
                                      <Link
                                        to={`/services/${cluster.pillar.slug}/${page.slug}`}
                                        className="text-xs text-muted-foreground hover:text-foreground transition-colors block py-0.5"
                                      >
                                        {page.h1}
                                      </Link>
                                    </NavigationMenuLink>
                                  </li>
                                ))}
                                {cluster.moneyPages.length > 4 && (
                                  <li>
                                    <Link
                                      to={`/services/${cluster.pillar.slug}`}
                                      className="text-xs text-primary hover:text-primary/80 transition-colors flex items-center gap-1 py-0.5"
                                    >
                                      View all <ArrowRight className="w-3 h-3" />
                                    </Link>
                                  </li>
                                )}
                              </ul>
                            </div>
                          ))}
                        </div>
                        <div className="mt-6 pt-4 border-t border-border flex justify-between items-center">
                          <Link 
                            to="/services" 
                            className="text-sm font-medium text-primary hover:text-primary/80 transition-colors flex items-center gap-1"
                          >
                            View All Services <ArrowRight className="w-4 h-4" />
                          </Link>
                          <Link 
                            to="/tools/ai-growth-audit" 
                            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                          >
                            Get a Free Audit →
                          </Link>
                        </div>
                      </div>
                      </NavigationMenuContent>
                    </NavigationMenuItem>
                  </NavigationMenuList>
                </NavigationMenu>
              </div>

              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`px-4 py-2 text-sm font-medium transition-colors hover:text-foreground ${
                    location.pathname === link.href
                      ? "text-foreground"
                      : "text-muted-foreground"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* CTA Button */}
            <div className="hidden md:flex items-center z-10">
              <Button 
                className="rounded-full px-6"
                data-cal-link="thecloudseo/30-min-meeting"
                data-cal-namespace="30-min-meeting"
                data-cal-config='{"layout":"month_view"}'
              >
                Book a Call
              </Button>
            </div>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 -mr-2"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </nav>
        </div>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 bg-background md:hidden overflow-y-auto"
          >
            <motion.nav
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              className="pt-24 pb-8 px-6"
            >
              {/* Services Accordion */}
              <div className="mb-4">
                <button
                  onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                  className="flex items-center justify-between w-full py-3 text-xl font-medium"
                >
                  Services
                  <ChevronDown className={cn("w-5 h-5 transition-transform", mobileServicesOpen && "rotate-180")} />
                </button>
                <AnimatePresence>
                  {mobileServicesOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="pl-4 space-y-4 py-4">
                        {pillars.map(({ cluster, icon: Icon, color, bgColor }) => (
                          <div key={cluster.pillar.slug} className="space-y-2">
                            <Link 
                              to={`/services/${cluster.pillar.slug}`}
                              className="flex items-center gap-2"
                            >
                              <div className={cn("p-1.5 rounded-md", bgColor)}>
                                <Icon className={cn("w-4 h-4", color)} />
                              </div>
                              <span className="font-medium">{cluster.pillar.title}</span>
                            </Link>
                            <ul className="pl-8 space-y-1">
                              {cluster.moneyPages.slice(0, 3).map((page) => (
                                <li key={page.slug}>
                                  <Link
                                    to={`/services/${cluster.pillar.slug}/${page.slug}`}
                                    className="text-sm text-muted-foreground block py-1"
                                  >
                                    {page.h1}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                        <Link 
                          to="/services"
                          className="text-primary font-medium flex items-center gap-1 pt-2"
                        >
                          View All Services <ArrowRight className="w-4 h-4" />
                        </Link>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Other Links */}
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`block py-3 text-xl font-medium transition-colors ${
                    location.pathname === link.href
                      ? "text-foreground"
                      : "text-muted-foreground"
                  }`}
                >
                  {link.label}
                </Link>
              ))}

              <div className="mt-8">
                <Button 
                  size="lg" 
                  className="rounded-full w-full"
                  data-cal-link="thecloudseo/30-min-meeting"
                  data-cal-namespace="30-min-meeting"
                  data-cal-config='{"layout":"month_view"}'
                >
                  Book a Call
                </Button>
              </div>
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
